import { decodeMqttText } from '@/contexts/mqtt/payload';

import {
    asBoolean,
    asFiniteNumber,
    asWinchState,
    isRecord,
    matchNamedPosition,
    parseCoords,
    parseLimits,
    parseNamedConfigPositions,
    parseSavedPositions,
    parseTravelLimits,
    unwrapData,
} from './winchMqttParseUtils';
import {
    type MqttCommandResult,
    type VerticalScanConfig,
    WINCH_STATES,
    type WinchMotionState,
    type WinchStatusSnapshot,
    type WinchTelemetry,
} from './winchMqttTypes';

export function parseWinchTelemetry(
    payload: unknown,
    previousState?: WinchMotionState | null
): WinchTelemetry | null {
    const data = unwrapData(payload);
    if (!data) {
        return null;
    }

    const distance = asFiniteNumber(data.calculated_distance);
    const depth = asFiniteNumber(data.calculated_depth) ?? asFiniteNumber(data.depth_sensor);
    if (distance == null && depth == null && data.state == null && data.calibrated == null) {
        return null;
    }

    return {
        state: asWinchState(data.state) ?? previousState ?? WINCH_STATES.idle,
        distance,
        depth,
        targetDistance: asFiniteNumber(data.current_x_target),
        targetDepth: asFiniteNumber(data.current_y_target),
        calibrated: asBoolean(data.calibrated, false),
        allowSmartMovement: asBoolean(data.allow_smart_movement, true),
        timestamp: typeof data.timestamp === 'string' ? data.timestamp : null,
    };
}

/** `v1/winch/{id}/getposition/response` — named presets. Travel limits are applied from status `strictlimits`. */
export function parseWinchGetPosition(payload: unknown): WinchStatusSnapshot | null {
    const data = unwrapData(payload);
    if (!data || !isRecord(data.position)) {
        return null;
    }

    const named = data.position;
    const savedPositions = parseNamedConfigPositions(named);
    const snapshot: WinchStatusSnapshot = {
        limits: parseLimits(data.limits),
        speed: isRecord(data.configuration) ? asFiniteNumber(data.configuration.speed) : null,
        savedPositions,
        measurementPosition: parseCoords(named.measurement),
        servicePosition: parseCoords(named.service),
    };

    if (
        snapshot.limits == null &&
        snapshot.speed == null &&
        snapshot.savedPositions.length === 0 &&
        snapshot.servicePosition == null &&
        snapshot.measurementPosition == null
    ) {
        return null;
    }

    return snapshot;
}

/**
 * Status snapshot parser — travel limits from `strictlimits`.
 * The new UI reads presets from getposition instead.
 */
export function parseWinchStatusSnapshot(payload: unknown): WinchStatusSnapshot | null {
    const data = unwrapData(payload);
    if (!data) {
        return null;
    }

    const configuration = isRecord(data.configuration) ? data.configuration : null;
    const fromObject = parseNamedConfigPositions(data.positions);
    const fromArray = parseSavedPositions(data.saved_positions);
    const savedPositions = fromObject.length > 0 ? fromObject : fromArray;
    const objectMeasurement = parseCoords(
        isRecord(data.positions) ? data.positions.measurement : null
    );
    const objectService = parseCoords(isRecord(data.positions) ? data.positions.service : null);

    const snapshot: WinchStatusSnapshot = {
        limits: parseTravelLimits(data),
        speed: configuration ? asFiniteNumber(configuration.speed) : null,
        savedPositions,
        servicePosition:
            objectService ?? matchNamedPosition(savedPositions, ['service', 'serviceposition']),
        measurementPosition:
            objectMeasurement ??
            matchNamedPosition(savedPositions, ['measurement', 'måling', 'maling']),
    };

    if (
        snapshot.limits == null &&
        snapshot.speed == null &&
        snapshot.savedPositions.length === 0 &&
        snapshot.servicePosition == null &&
        snapshot.measurementPosition == null
    ) {
        return null;
    }

    return snapshot;
}

/**
 * `data.verticalScan` on status / enable / disable / setlimits responses.
 * Returns null unless enabled, yMin, and yMax are present.
 */
export function parseVerticalScan(payload: unknown): VerticalScanConfig | null {
    const data = unwrapData(payload);
    if (!data) {
        return null;
    }
    const raw = isRecord(data.verticalScan) ? data.verticalScan : data;
    if (!isRecord(raw) || typeof raw.enabled !== 'boolean') {
        return null;
    }
    const yMin = asFiniteNumber(raw.yMin);
    const yMax = asFiniteNumber(raw.yMax);
    if (yMin == null || yMax == null) {
        return null;
    }

    const percentageFromMiddle = asFiniteNumber(raw.percentageFromMiddle);
    const totalSweepTime = asFiniteNumber(raw.totalSweepTime);
    const stepMoveTime = asFiniteNumber(raw.stepMoveTime);
    const startDelay = asFiniteNumber(raw.startDelay);

    return {
        enabled: raw.enabled,
        x: asFiniteNumber(raw.x),
        yMin,
        yMax,
        ...(percentageFromMiddle != null ? { percentageFromMiddle } : {}),
        ...(totalSweepTime != null ? { totalSweepTime } : {}),
        ...(stepMoveTime != null ? { stepMoveTime } : {}),
        ...(startDelay != null ? { startDelay } : {}),
    };
}

export function parseMqttCommandResponse(payload: unknown): MqttCommandResult {
    if (!isRecord(payload)) {
        return { success: false };
    }
    const status = isRecord(payload.status) ? payload.status : payload;
    const errorCode = asFiniteNumber(status.error_code);
    const uuid = typeof payload.uuid === 'string' && payload.uuid !== '' ? payload.uuid : undefined;
    return {
        success: errorCode === 0,
        description: typeof status.description === 'string' ? status.description : undefined,
        ...(uuid ? { uuid } : {}),
    };
}

/**
 * Parse the retained `v1/winch/{id}/online` payload.
 * IPS uses the strings `online` / `offline`. Older firmware used 0x01 / ASCII '1'.
 * Empty payload (retained-topic clear) returns null — leave current state unchanged.
 */
export function parseWinchOnlinePayload(message: Uint8Array): boolean | null {
    if (message.length === 0) {
        return null;
    }

    const text = decodeMqttText(message).trim().toLowerCase();
    if (text === 'online') {
        return true;
    }
    if (text === 'offline') {
        return false;
    }

    const byte = message[0];
    if (byte === 1 || byte === 0x31) {
        return true;
    }
    if (byte === 0 || byte === 0x30) {
        return false;
    }

    return null;
}
